import { Link, useLocation } from 'react-router-dom';

/**
 * Breadcrumbs — orientation trail for every authenticated page.
 * Exact paths match against the table below first; dynamic detail routes
 * (record ids) match the pattern table. Dynamic segments collapse to a
 * generic label so trails never leak raw ids.
 */

interface Crumb {
  label: string;
  to?: string;
}

const single = (label: string): Crumb[] => [{ label }];
const child = (parentLabel: string, parentTo: string, label: string): Crumb[] => [
  { label: parentLabel, to: parentTo },
  { label },
];

const EXACT: Record<string, Crumb[]> = {
  // --- How-To ---
  '/app/how-to': single('How-To Videos'),
  // --- Org workflow ---
  '/app/overview': single('Overview'),
  '/app/dashboard': single('Overview'),
  '/app/discover': single('Discover'),
  '/app/opportunities': single('Discover'),
  '/app/opportunities/new': child('Discover', '/app/discover', 'New opportunity'),
  '/app/analyze': single('Analyze'),
  '/app/saved': single('Saved'),
  '/app/pipeline': single('Pipeline'),
  '/app/portfolio': single('Portfolio'),
  '/app/proposals': single('Proposals'),
  '/app/awards': single('Awards'),
  '/app/readiness': single('Readiness'),
  '/app/govcon-readiness': child('Readiness', '/app/readiness', 'GovCon checklist'),
  '/app/organization': single('Your Organization DNA'),
  '/app/profile': single('Organization'),
  '/app/vault': single('Vault'),
  '/app/documents': single('Documents'),
  '/app/registrations': single('Registrations'),
  '/app/activity': single('Activity'),
  '/app/tasks': single('Tasks'),
  '/app/calendar': single('Calendar'),
  '/app/agents': single('AI Teammates'),
  '/app/team': single('Team'),
  '/app/stakeholders': single('Team'),
  '/app/reports': single('Reports'),
  '/app/analytics': single('Reports'),
  '/app/billing': single('Billing'),
  '/app/settings': single('Settings'),
  '/app/onboarding': single('Onboarding'),
  // --- Individual workflow (the shared "Home" crumb already points to /app/individual) ---
  '/app/individual': [],
  '/app/individual/onboarding': single('Onboarding'),
  '/app/individual/discover': single('Discover'),
  '/app/individual/applications': single('Applications'),
  '/app/individual/profile': single('My profile'),
};

const PATTERNS: { re: RegExp; trail: Crumb[] }[] = [
  { re: /^\/app\/opportunities\/[^/]+\/edit$/, trail: child('Discover', '/app/discover', 'Edit opportunity') },
  { re: /^\/app\/opportunities\/[^/]+$/, trail: child('Discover', '/app/discover', 'Opportunity') },
  { re: /^\/app\/analyze\/.+$/, trail: single('Analyze') },
  { re: /^\/app\/proposals\/opp\/[^/]+$/, trail: child('Proposals', '/app/proposals', 'New proposal') },
  { re: /^\/app\/proposals\/[^/]+$/, trail: child('Proposals', '/app/proposals', 'Workspace') },
  { re: /^\/app\/awards\/[^/]+$/, trail: child('Awards', '/app/awards', 'Award') },
  { re: /^\/app\/agents\/[^/]+$/, trail: child('AI Teammates', '/app/agents', 'Assistant') },
  { re: /^\/app\/individual\/discover\/[^/]+$/, trail: child('Discover', '/app/individual/discover', 'Opportunity') },
  { re: /^\/app\/individual\/applications\/[^/]+$/, trail: child('Applications', '/app/individual/applications', 'Application') },
];

function trailFor(pathname: string): Crumb[] | null {
  const clean = pathname.replace(/\/+$/, '') || '/';
  if (clean in EXACT) return EXACT[clean];
  for (const { re, trail } of PATTERNS) {
    if (re.test(clean)) return trail;
  }
  return null;
}

export default function Breadcrumbs() {
  const { pathname } = useLocation();
  const trail = trailFor(pathname);
  if (trail === null || trail.length === 0) return null;

  const isIndividual = pathname.startsWith('/app/individual');
  const homeTo = isIndividual ? '/app/individual' : '/app/overview';

  return (
    <nav aria-label="Breadcrumb" className="breadcrumbs">
      <ol>
        <li>
          <Link to={homeTo}>Home</Link>
        </li>
        {trail.map((crumb, i) => {
          const isLast = i === trail.length - 1;
          return (
            <li key={`${crumb.label}-${i}`} aria-current={isLast ? 'page' : undefined}>
              {isLast || !crumb.to ? (
                <span className={isLast ? 'crumb-current' : undefined}>{crumb.label}</span>
              ) : (
                <Link to={crumb.to}>{crumb.label}</Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
